import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Text } from "@jobteaser/spark/components/Text"
import { ElementGrid } from "@jobteaser/ui-kit/components/ElementGrid/ElementGrid"
import { Logo } from "@jobteaser/ui-kit/components/Image/Logo"

import { HeaderActions } from "@/features/companyDetail/components/Layout/HeaderActions/HeaderActions"
import { HeaderKeyInformation } from "@/features/companyDetail/components/Layout/HeaderKeyInformation/HeaderKeyInformation"
import { SeoSchemaAndMeta } from "@/features/companyDetail/components/Layout/SeoSchemaAndMetatag/SeoSchemaAndMeta"
import { SocialNetworks } from "@/features/companyDetail/components/Layout/SocialNetworks/SocialNetworks"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { MEDIA_TYPE_IMAGE } from "@/modules/companies/constants/enums"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import { useTranslation } from "@/modules/i18n/components/useTranslation"

import styles from "./Header.module.css"

const ImagesGalleryDynamic = dynamic(() =>
  import("@/features/companyDetail/components/shared/MediaGallery/ImagesGallery").then(mod => mod.ImagesGallery)
)

const TagUniversityDynamic = dynamic(() =>
  import("@jobteaser/ui-kit/components/Tag/TagUniversity").then(mod => mod.TagUniversity)
)

type HeaderProps = {
  baseUrl: string
}

export const Header: FunctionComponent<HeaderProps> = ({ baseUrl }) => {
  const { t } = useTranslation(["companies_client"])
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()

  if (!dataCompany) return null

  const {
    id,
    page: { businessType, description, gallery, headquarterAddress, logoUrl, name, partnership, sector },
  } = dataCompany
  const headerKeyInformationProps = { businessType, headquarterAddress, sector }

  const displayPartnerLabel = dataCareerCenter?.intranet && !!partnership.schoolId
  const displayGalleryImage = gallery.filter(currentMedia => currentMedia.type === MEDIA_TYPE_IMAGE).length > 0

  return (
    <header data-testid="company_header">
      <SeoSchemaAndMeta baseUrl={baseUrl} />
      <ElementGrid
        className={clsx(styles.header__container, {
          [styles["header__container--withGallery"]]: displayGalleryImage,
        })}
        itemTag="div"
        wrapperTag="div"
      >
        <section className={styles.header__mainInformation}>
          <div className={styles.header__branding}>
            <Logo
              alt="" // no alt needed here, the important info is just after (company name) // https://www.w3.org/WAI/tutorials/images/functional/#example-2-logo-image-within-link-text
              boxSize="medium"
              className={styles.header__logo}
              data-testid="company_header_logo"
              loading="eager"
              onLoadErrorPath=""
              src={logoUrl}
            />
            <SocialNetworks />
          </div>
          <div className={styles.header__entity}>
            {displayPartnerLabel && (
              <TagUniversityDynamic
                label={t("companies_client.header.partnerCompany")}
                universityLogo={dataCareerCenter.logo}
              />
            )}
            <Heading as="h1" data-testid="company_header_name" variant="title1" weight="bold">
              {name}
            </Heading>
            <Text data-testid="company_header_description" variant="body1" weight="regular">
              {description}
            </Text>
          </div>
          <HeaderActions />
        </section>
        {displayGalleryImage && (
          <section className={styles.header__gallery} data-testid="company_header_gallery">
            <ImagesGalleryDynamic companyGallery={gallery} companyId={id} companyName={name} />
          </section>
        )}
      </ElementGrid>
      <HeaderKeyInformation {...headerKeyInformationProps} />
    </header>
  )
}
